import { ASSET_SOURCE_PIXELS_PER_METRE } from '../reference/asset-source-scale.js';
import {
  SKILL_ASSET_SPECS_V1,
  type SkillAssetCatalogEntryV1, type SkillAssetRoleV1, type SkillAssetSpecV1,
  type SkillVisualBindingV1, type SkillVisualProfileV1, type StageVisualProfileV1,
} from '../reference/skill-asset-specs-v1.js';

export const SKILL_ASSET_URL_PREFIX = '/assets/skills/';

/**
 * Maps the opaque wire identity to role bindings. Returning undefined keeps
 * the procedural drawing; no implementation may substitute another profile
 * for one it does not know.
 */
export interface SkillVisualProfileResolver {
  stage(visualProfileId: string, stageId: string): StageVisualProfileV1 | undefined;
  cast(visualProfileId: string): SkillVisualBindingV1<'CAST'> | undefined;
}

/** Production resolver: exact profile and stage lookup in a registry. */
export function registryProfileResolver(profiles: readonly SkillVisualProfileV1[]): SkillVisualProfileResolver {
  const byId = new Map<string, SkillVisualProfileV1>();
  for (const profile of profiles) {
    // A second definition must not silently replace the first.
    if (byId.has(profile.profileId)) throw new Error(`duplicate skill visual profile ${profile.profileId}`);
    byId.set(profile.profileId, profile);
  }
  return {
    stage: (visualProfileId, stageId) => {
      const stages = byId.get(visualProfileId)?.stages;
      return stages !== undefined && Object.hasOwn(stages, stageId) ? stages[stageId] : undefined;
    },
    cast: (visualProfileId) => byId.get(visualProfileId)?.cast,
  };
}

const PROFILE_SLOTS = ['BODY', 'AREA', 'IMPACT', 'TRAIL'] as const;
const isBinding = (value: unknown, role: string): boolean => typeof value === 'object' && value !== null
  && (value as { role?: unknown }).role === role && typeof (value as { assetKey?: unknown }).assetKey === 'string';

/** Accepts an untrusted profile only when every binding sits in the slot of its own role. */
export function parseSkillVisualProfile(value: unknown): SkillVisualProfileV1 | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { profileId, cast, stages } = value as { profileId?: unknown; cast?: unknown; stages?: unknown };
  if (typeof profileId !== 'string' || (cast !== undefined && !isBinding(cast, 'CAST'))) return undefined;
  if (typeof stages !== 'object' || stages === null || Array.isArray(stages)) return undefined;
  for (const stage of Object.values(stages)) {
    if (typeof stage !== 'object' || stage === null) return undefined;
    for (const [slot, binding] of Object.entries(stage)) {
      if (!(PROFILE_SLOTS as readonly string[]).includes(slot) || !isBinding(binding, slot)) return undefined;
    }
  }
  return value as SkillVisualProfileV1;
}

export interface RemoteProfileSource {
  /** The server's profile list for skills in play and its conflict tombstones (untrusted). */
  fetchProfiles(): Promise<{ readonly profiles: readonly unknown[]; readonly conflictedProfileIds: readonly unknown[] }>;
  now(): number;
}

/** How long an id missing from a successful list stays unrequested. */
const ABSENT_PROFILE_TTL_MS = 30_000;

/**
 * Resolves profiles the server reports for skills in play. The first unknown
 * id fetches the whole list, and later unknown ids refetch at most once per
 * interval. An id asked for but missing from a successful list is not asked
 * for again for a while, so ids that are never skills (basic attacks, monster
 * shots) do not keep the list refetching. Until a list includes an id it
 * resolves to undefined. An invalid profile is ignored; an id the server
 * tombstones, or one that ever arrives with two different profiles, is
 * dropped and rejected for good rather than last-wins.
 */
export function remoteProfileResolver(source: RemoteProfileSource, refreshIntervalMs = 2_000): SkillVisualProfileResolver {
  const known = new Map<string, { readonly profile: SkillVisualProfileV1; readonly canonical: string }>();
  const conflicted = new Set<string>();
  const absentSince = new Map<string, number>();
  let unresolved = new Set<string>();
  let lastRequestAt = -Infinity; let inFlight = false;
  const reject = (id: string): void => { known.delete(id); conflicted.add(id); };
  const accept = (requested: ReadonlySet<string>, at: number, list: { readonly profiles: readonly unknown[]; readonly conflictedProfileIds: readonly unknown[] }): void => {
    for (const id of list.conflictedProfileIds) if (typeof id === 'string') reject(id);
    for (const value of list.profiles) {
      const profile = parseSkillVisualProfile(value);
      if (profile === undefined || conflicted.has(profile.profileId)) continue;
      const canonical = JSON.stringify(profile);
      const existing = known.get(profile.profileId);
      if (existing === undefined) known.set(profile.profileId, { profile, canonical });
      else if (existing.canonical !== canonical) reject(profile.profileId);
    }
    for (const id of requested) if (!known.has(id) && !conflicted.has(id)) absentSince.set(id, at);
  };
  const lookup = (visualProfileId: string): SkillVisualProfileV1 | undefined => {
    const found = known.get(visualProfileId)?.profile;
    if (found !== undefined || conflicted.has(visualProfileId)) return found;
    const now = source.now();
    const absent = absentSince.get(visualProfileId);
    if (absent !== undefined && now - absent < ABSENT_PROFILE_TTL_MS) return undefined;
    unresolved.add(visualProfileId);
    if (!inFlight && now - lastRequestAt >= refreshIntervalMs) {
      inFlight = true; lastRequestAt = now;
      const requested = unresolved; unresolved = new Set();
      source.fetchProfiles().then((list) => accept(requested, now, list),
        () => { for (const id of requested) unresolved.add(id); }).finally(() => { inFlight = false; });
    }
    return undefined;
  };
  return {
    stage: (visualProfileId, stageId) => {
      const stages = lookup(visualProfileId)?.stages;
      return stages !== undefined && Object.hasOwn(stages, stageId) ? stages[stageId] : undefined;
    },
    cast: (visualProfileId) => lookup(visualProfileId)?.cast,
  };
}

/**
 * Diagnostic resolver: every profile and stage reads the fixture's single
 * stage. Selected only by an explicit diagnostic mode, never as a fallback.
 */
export function diagnosticProfileResolver(fixture: SkillVisualProfileV1): SkillVisualProfileResolver {
  const stages = Object.values(fixture.stages);
  if (stages.length !== 1) throw new Error('a diagnostic fixture declares exactly one stage');
  const stage = stages[0]!;
  return { stage: () => stage, cast: () => fixture.cast };
}

export interface LoadedSkillAsset { readonly image: unknown; readonly spec: SkillAssetSpecV1 }
/** Returns an asset only once its image has loaded and only for a binding of the catalogued role. */
export type SkillAssetLookup = (binding: SkillVisualBindingV1) => LoadedSkillAsset | undefined;

interface LoadableImage { src: string; onload: ((...args: never[]) => unknown) | null; onerror: ((...args: never[]) => unknown) | null }

type RoleMismatchReport = (binding: SkillVisualBindingV1, catalogued: SkillAssetRoleV1) => void;

/**
 * Validates the whole catalog up front (a duplicate key throws before any
 * download) and resolves a binding to its asset once the image has loaded.
 * A binding naming an unknown key or a key of another role resolves to
 * nothing, so the caller draws its procedural shape; a role mismatch is also
 * reported once per key and role. `eager` starts every download at once;
 * otherwise an image is fetched on the first valid lookup of its key.
 */
function skillAssetLookup(
  catalog: readonly SkillAssetCatalogEntryV1[], createImage: () => LoadableImage,
  reportRoleMismatch: RoleMismatchReport, eager: boolean,
): SkillAssetLookup {
  const entries = new Map<string, SkillAssetCatalogEntryV1>();
  for (const entry of catalog) {
    if (entries.has(entry.assetKey)) throw new Error(`duplicate skill asset key ${entry.assetKey}`);
    entries.set(entry.assetKey, entry);
  }
  const started = new Set<string>();
  const ready = new Map<string, LoadedSkillAsset>();
  const reported = new Set<string>();
  const load = (entry: SkillAssetCatalogEntryV1): void => {
    started.add(entry.assetKey);
    const image = createImage();
    image.onload = () => { ready.set(entry.assetKey, { image, spec: SKILL_ASSET_SPECS_V1[entry.role] }); };
    image.onerror = () => { ready.delete(entry.assetKey); };
    image.src = SKILL_ASSET_URL_PREFIX + entry.file;
  };
  if (eager) for (const entry of catalog) load(entry);
  return (binding) => {
    const entry = entries.get(binding.assetKey);
    if (entry === undefined) return undefined;
    if (entry.role !== binding.role) {
      const key = `${binding.assetKey}\u0000${binding.role}`;
      if (!reported.has(key)) { reported.add(key); reportRoleMismatch(binding, entry.role); }
      return undefined;
    }
    if (!started.has(entry.assetKey)) load(entry);
    return ready.get(binding.assetKey);
  };
}

/** Starts every catalogued download at once; for the small diagnostic fixture. */
export function preloadSkillAssets(
  catalog: readonly SkillAssetCatalogEntryV1[], createImage: () => LoadableImage,
  reportRoleMismatch: RoleMismatchReport = () => undefined,
): SkillAssetLookup {
  return skillAssetLookup(catalog, createImage, reportRoleMismatch, true);
}

/**
 * Loads each image only when a profile first binds it, so a large catalog
 * costs nothing for art no skill in play uses. A failed image is not retried.
 */
export function lazySkillAssets(
  catalog: readonly SkillAssetCatalogEntryV1[], createImage: () => LoadableImage,
  reportRoleMismatch: RoleMismatchReport = () => undefined,
): SkillAssetLookup {
  return skillAssetLookup(catalog, createImage, reportRoleMismatch, false);
}

export interface SkillVisuals { readonly profiles: SkillVisualProfileResolver; readonly assets: SkillAssetLookup }
/** Empty registry, no assets: every skill keeps its procedural drawing. */
export const PROCEDURAL_SKILL_VISUALS: SkillVisuals = Object.freeze({ profiles: registryProfileResolver([]), assets: () => undefined });

export interface SkillAssetSurface {
  save(): void; restore(): void; translate(x: number, y: number): void; rotate(angle: number): void;
  globalAlpha: number;
  drawImage?(image: never, dx: number, dy: number, dw: number, dh: number): void;
}

/** Rotation that turns up-facing (−Y) art toward a direction. */
export function facingRotation(direction: { readonly x: number; readonly y: number } | undefined): number {
  return direction !== undefined && (direction.x !== 0 || direction.y !== 0) ? Math.atan2(direction.y, direction.x) + Math.PI / 2 : 0;
}

/**
 * Draws one asset with its pivot on `screen`. SCALE_TO_RADIUS art is
 * stretched so its reference radius matches `liveRadiusM`; FIXED art keeps
 * source scale. Returns false when the surface cannot draw images.
 */
export function drawSkillAsset(
  ctx: SkillAssetSurface, asset: LoadedSkillAsset, screen: { readonly x: number; readonly y: number },
  pixelsPerMetre: number, liveRadiusM: number, rotationRad: number, alpha = 1,
): boolean {
  if (typeof ctx.drawImage !== 'function') return false;
  const { spec } = asset;
  const scale = pixelsPerMetre / ASSET_SOURCE_PIXELS_PER_METRE
    * (spec.sizeMode === 'SCALE_TO_RADIUS' ? liveRadiusM / spec.referenceRadiusM : 1);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(screen.x, screen.y);
  ctx.rotate(rotationRad);
  ctx.drawImage(asset.image as never, -spec.pivotPx[0] * scale, -spec.pivotPx[1] * scale, spec.canvasPx[0] * scale, spec.canvasPx[1] * scale);
  ctx.restore();
  return true;
}
